import { useEffect, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { toast } from '@/hooks/use-toast'
import {
  useBlockDefaultSettings,
  useBlocksWithSettings,
  useCreateWorkflowBlockSettings,
  useDeleteWorkflowBlockSettings,
  useUpdateWorkflowBlockSettings,
  useWorkflowBlockSettings,
} from '@/features/admin/workflowSetupQueries'
import type { AdminWorkflowBlockSettings } from '@/types/admin'
import type { ApiRequestError } from '@/services/api'

function pretty(value: unknown): string {
  return JSON.stringify(value, null, 2)
}

interface BlockSettingsSectionProps {
  organizationId: string
  workflowId: string
  isRoot: boolean
}

/**
 * A workflow's per-block settings: create one for any block with a known
 * settings shape (blocks/*.blockSettings, via internal/block/settings on the
 * backend), edit or delete existing rows. Only root may write — a block's
 * settings are how it behaves technically, the same category as the flow
 * above this section, not a partner's own mailbox/ERP credentials.
 */
export function BlockSettingsSection({ organizationId, workflowId, isRoot }: BlockSettingsSectionProps) {
  const { data: rows } = useWorkflowBlockSettings(organizationId, workflowId)
  const { data: blockNames } = useBlocksWithSettings()
  const create = useCreateWorkflowBlockSettings(organizationId, workflowId)

  const [selectedBlock, setSelectedBlock] = useState<string | null>(null)
  const { data: defaultSettings } = useBlockDefaultSettings(selectedBlock)
  const [newText, setNewText] = useState('')
  const [newError, setNewError] = useState<string | null>(null)

  useEffect(() => {
    if (selectedBlock && defaultSettings !== undefined) setNewText(pretty(defaultSettings))
  }, [selectedBlock, defaultSettings])

  const configuredBlockNames = new Set((rows ?? []).map((row) => row.block_name))
  const availableBlockNames = (blockNames ?? []).filter((name) => !configuredBlockNames.has(name))

  function selectBlock(name: string) {
    setSelectedBlock(name)
    setNewError(null)
  }

  async function createSetting() {
    if (!selectedBlock) return

    let parsed: unknown
    try {
      parsed = JSON.parse(newText)
    } catch (e) {
      setNewError((e as Error).message)
      return
    }

    try {
      await create.mutateAsync({ block_name: selectedBlock, settings: parsed })
      toast({ title: 'Block setting created' })
      setSelectedBlock(null)
      setNewText('')
      setNewError(null)
    } catch (e) {
      setNewError((e as ApiRequestError)?.message ?? 'Could not create the setting')
    }
  }

  return (
    <div className="mt-8 border-t border-gray-100 pt-6">
      <h3 className="text-sm font-semibold text-gray-900">Block settings</h3>
      <p className="mt-1 text-xs text-gray-400">
        Per-block configuration for this workflow. Only blocks with a known settings shape can be
        added.
      </p>

      {isRoot && (
        <div className="mt-4 space-y-2 rounded-md border border-gray-100 p-4">
          <div className="flex items-center gap-2">
            <Select value={selectedBlock ?? ''} onValueChange={selectBlock}>
              <SelectTrigger className="h-9 w-64">
                <SelectValue placeholder="Select a block">{selectedBlock}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {availableBlockNames.length === 0 ? (
                  <p className="px-2 py-1.5 text-xs text-gray-400">
                    Every known block already has settings here.
                  </p>
                ) : (
                  availableBlockNames.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Button onClick={createSetting} disabled={!selectedBlock || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create setting'}
            </Button>
          </div>

          {selectedBlock && (
            <textarea
              value={newText}
              onChange={(e) => {
                setNewText(e.target.value)
                setNewError(null)
              }}
              spellCheck={false}
              rows={8}
              className="w-full rounded-md border border-input bg-white p-3 font-mono text-xs text-gray-800 focus:border-primary focus:outline-none"
            />
          )}
          {newError && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{newError}</p>
          )}
        </div>
      )}

      <div className="mt-4 space-y-3">
        {(rows ?? []).length === 0 && (
          <p className="text-xs text-gray-400">No block settings configured for this workflow.</p>
        )}
        {rows?.map((row) => (
          <BlockSettingsRow
            key={row.block_name}
            organizationId={organizationId}
            workflowId={workflowId}
            row={row}
            isRoot={isRoot}
          />
        ))}
      </div>
    </div>
  )
}

function BlockSettingsRow({
  organizationId,
  workflowId,
  row,
  isRoot,
}: {
  organizationId: string
  workflowId: string
  row: AdminWorkflowBlockSettings
  isRoot: boolean
}) {
  const update = useUpdateWorkflowBlockSettings(organizationId, workflowId)
  const remove = useDeleteWorkflowBlockSettings(organizationId, workflowId)

  const [text, setText] = useState(() => pretty(row.settings))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setText(pretty(row.settings))
  }, [row.settings])

  async function save() {
    let parsed: unknown
    try {
      parsed = JSON.parse(text)
    } catch (e) {
      setError((e as Error).message)
      return
    }

    try {
      await update.mutateAsync({ blockName: row.block_name, settings: parsed })
      toast({ title: 'Block setting saved' })
      setError(null)
    } catch (e) {
      setError((e as ApiRequestError)?.message ?? 'Could not save the setting')
    }
  }

  async function deleteSetting() {
    if (!window.confirm(`Delete the settings for ${row.block_name}?`)) return
    try {
      await remove.mutateAsync(row.block_name)
      toast({ title: 'Block setting deleted' })
    } catch (e) {
      toast({
        variant: 'destructive',
        description: (e as ApiRequestError)?.message ?? 'Could not delete the setting',
      })
    }
  }

  return (
    <div className="rounded-md border border-gray-200 p-4">
      <div className="mb-2 flex items-center justify-between">
        <p className="font-mono text-xs font-semibold text-gray-700">{row.block_name}</p>
        {isRoot && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={save} disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={deleteSetting}
              disabled={remove.isPending}
              aria-label={`Delete settings for ${row.block_name}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          setError(null)
        }}
        readOnly={!isRoot}
        spellCheck={false}
        rows={8}
        className="w-full rounded-md border border-input bg-white p-3 font-mono text-xs text-gray-800 read-only:bg-gray-50 read-only:text-gray-500 focus:border-primary focus:outline-none"
      />
      {error && <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
    </div>
  )
}
